import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { SERVICES } from "../../lib/services";
import { Loader2, Save, RotateCcw, Type, Check } from "lucide-react";
import BusinessHoursEditor from "./BusinessHoursEditor";
import FaqListEditor from "./FaqListEditor";
import AboutEditor from "./AboutEditor";
import HowItWorksEditor from "./HowItWorksEditor";
import AnnouncementEditor from "./AnnouncementEditor";
import GoogleReviewsEditor from "./GoogleReviewsEditor";

/**
 * Admin Site Text Editor — live edits of Hero, Packages, Services taglines.
 * All fields persist in MongoDB (site_text_config) and reflect on the public
 * site after refresh (or instantly for users who load the page after save).
 */
const SECTIONS = [
  {
    key: "hero",
    label: "Úvodní sekce (Hero)",
    fields: [
      { key: "eyebrow", label: "Štítek nad nadpisem", multiline: false },
      { key: "headline_line1", label: "Nadpis řádek 1", multiline: false },
      { key: "headline_line2", label: "Nadpis řádek 2 (zlatý)", multiline: false },
      { key: "headline_line3", label: "Nadpis řádek 3 (shimmer)", multiline: false },
      { key: "subheadline", label: "Podtitulek (delší text)", multiline: true },
      { key: "cta_primary", label: "Hlavní tlačítko (Rezervovat)", multiline: false },
      { key: "cta_secondary", label: "Druhé tlačítko (Služby)", multiline: false },
    ],
  },
  {
    key: "packages",
    label: "Výhodné balíčky",
    fields: [
      { key: "title", label: "Nadpis sekce", multiline: false },
      { key: "subtitle", label: "Podtitulek", multiline: true },
      { key: "phone_cta_label", label: "Text tlačítka „Zavolejte nám\"", multiline: false },
      { key: "footer_note", label: "Poznámka pod kartami", multiline: true },
    ],
  },
  {
    key: "packages_teaser",
    label: "Balíčky — upoutávka na hlavní stránce",
    fields: [
      { key: "title", label: "Nadpis upoutávky", multiline: false },
      { key: "description", label: "Krátký popis", multiline: true },
      { key: "button_label", label: "Text tlačítka", multiline: false },
    ],
  },
  {
    key: "services",
    label: "Služby — taglines",
    fields: [
      { key: "exterior_tagline", label: "Exteriér tagline", multiline: true },
      { key: "interior_tagline", label: "Interiér tagline", multiline: true },
      { key: "complete_tagline", label: "Komplet tagline", multiline: true },
    ],
  },
];

export default function SiteTextEditorPanel() {
  const [sections, setSections] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/site-text/public")
      .then((r) => setSections(r.data?.sections || {}))
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const setField = (section, key, value) => {
    setSections((s) => ({ ...s, [section]: { ...(s?.[section] || {}), [key]: value } }));
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const { data } = await api.put("/site-text", { sections });
      setSections(data?.sections || sections);
      setMsg({ kind: "ok", text: "Texty uloženy. Změny uvidíš po obnovení stránky." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Uložení selhalo." });
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!confirm("Opravdu vrátit všechny texty na výchozí?")) return;
    setResetting(true);
    setMsg(null);
    try {
      const { data } = await api.post("/site-text/reset");
      setSections(data?.sections || {});
      setMsg({ kind: "ok", text: "Texty vráceny na výchozí hodnoty." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Reset selhal." });
    } finally {
      setResetting(false);
    }
  };

  if (loading || !sections) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;

  return (
    <div data-testid="site-text-editor">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <h3 className="font-display text-2xl text-apex-cream font-bold flex items-center gap-2">
            <Type className="w-5 h-5 text-apex-gold" />
            Texty webu
          </h3>
          <p className="text-apex-cream/60 text-sm mt-1">
            Upravte úvodní nadpisy, texty Balíčků a krátké popisky služeb. Změny jsou viditelné okamžitě po uložení.
          </p>
        </div>
        <div className="flex gap-2 flex-shrink-0">
          <button
            onClick={reset}
            disabled={resetting || saving}
            className="text-xs px-3 py-2 rounded-lg border border-apex-line text-apex-cream/70 hover:border-apex-gold/40 hover:text-apex-cream disabled:opacity-50 inline-flex items-center gap-1.5"
            data-testid="site-text-reset-btn"
          >
            <RotateCcw className="w-3.5 h-3.5" /> {resetting ? "Resetuje…" : "Reset"}
          </button>
          <button
            onClick={save}
            disabled={saving || resetting}
            className="btn-gold !py-2 !px-4 text-sm disabled:opacity-50"
            data-testid="site-text-save-btn"
          >
            <Save className="w-4 h-4" /> {saving ? "Ukládá…" : "Uložit změny"}
          </button>
        </div>
      </div>

      {msg && (
        <div
          className={`mb-4 rounded-lg px-4 py-2 text-sm ${
            msg.kind === "ok" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"
          }`}
          data-testid="site-text-msg"
        >
          {msg.kind === "ok" && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
          {msg.text}
        </div>
      )}

      <div className="space-y-6">
        {SECTIONS.map((section) => (
          <div key={section.key} className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid={`section-${section.key}`}>
            <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-4">{section.label}</div>
            <div className="space-y-4">
              {section.fields.map((field) => {
                const value = sections[section.key]?.[field.key] ?? "";
                const testId = `text-${section.key}-${field.key}`;
                return (
                  <label key={field.key} className="block">
                    <div className="text-xs text-apex-cream/70 mb-1">{field.label}</div>
                    {field.multiline ? (
                      <textarea
                        value={value}
                        rows={3}
                        onChange={(e) => setField(section.key, field.key, e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60 resize-y"
                        data-testid={testId}
                      />
                    ) : (
                      <input
                        type="text"
                        value={value}
                        onChange={(e) => setField(section.key, field.key, e.target.value)}
                        className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
                        data-testid={testId}
                      />
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ))}

        {/* Per-service detail editor — admin can rewrite tagline + features
            (4 bullets on home cards) + detailFeatures (full list in detail modal)
            for any of the 6 services. Empty bullet rows are stripped on save. */}
        <ServicesItemsEditor
          items={sections.services_items || {}}
          onChange={(next) => setSections((s) => ({ ...s, services_items: next }))}
        />

        {/* Opening hours — drives the booking calendar's available slots. */}
        <BusinessHoursEditor
          hours={sections.business_hours || {}}
          onChange={(next) => setSections((s) => ({ ...s, business_hours: next }))}
        />

        {/* Announcement banner — shown under Hero when active. */}
        <AnnouncementEditor
          data={sections.announcement_banner || {}}
          onChange={(next) => setSections((s) => ({ ...s, announcement_banner: next }))}
        />

        {/* About Me — name/role/intro + extra highlight boxes. */}
        <AboutEditor
          about={sections.about_section || {}}
          onChange={(next) => setSections((s) => ({ ...s, about_section: next }))}
        />

        {/* How It Works — title/subtitle + fully editable steps. */}
        <HowItWorksEditor
          data={sections.how_it_works || {}}
          onChange={(next) => setSections((s) => ({ ...s, how_it_works: next }))}
        />

        {/* FAQ — fully editable question/answer list. */}
        <FaqListEditor
          items={sections.faq_list || []}
          onChange={(next) => setSections((s) => ({ ...s, faq_list: next }))}
        />

        {/* Google recenze — skóre/počet/ukázkové recenze (rámeček pod galerií). */}
        <GoogleReviewsEditor
          data={sections.google_reviews || {}}
          onChange={(next) => setSections((s) => ({ ...s, google_reviews: next }))}
        />
      </div>

      {/* Packages editor — saved via separate endpoint /api/admin/packages */}
      <div className="mt-6">
        <PackagesEditor />
      </div>
    </div>
  );
}

// ── Services items editor ──────────────────────────────────────────────────
const SERVICE_ITEMS = [
  { key: "classic_exterior", label: "Exteriér Classic", tier: "classic", slot: "exterior" },
  { key: "classic_interior", label: "Interiér Classic", tier: "classic", slot: "interior" },
  { key: "classic_complete", label: "Komplet Classic", tier: "classic", slot: "complete" },
  { key: "detailing_exterior", label: "Exteriér Premium", tier: "detailing", slot: "exterior" },
  { key: "detailing_interior", label: "Interiér Premium", tier: "detailing", slot: "interior" },
  { key: "detailing_complete", label: "Komplet Premium", tier: "detailing", slot: "complete" },
];

/**
 * Returns the default static text for a given service key — used both to
 * pre-populate the editor (so admin sees current bullets right away and can
 * edit/remove them individually) and to power the "Vrátit výchozí" button.
 */
function getServiceDefaults(svcKey) {
  const meta = SERVICE_ITEMS.find((s) => s.key === svcKey);
  if (!meta) return { tagline: "", features: [], detailFeatures: [] };
  const src = SERVICES[meta.tier]?.[meta.slot] || {};
  return {
    tagline: src.tagline || "",
    features: Array.isArray(src.features) ? src.features.slice() : [],
    detailFeatures: Array.isArray(src.detailFeatures) ? src.detailFeatures.slice() : [],
  };
}

function ServicesItemsEditor({ items, onChange }) {
  const [openKey, setOpenKey] = useState(null);

  const getItem = (key) => items[key] || { tagline: "", features: [], detailFeatures: [] };
  const setItem = (key, patch) => onChange({ ...items, [key]: { ...getItem(key), ...patch } });

  // When user opens an expander for the first time and the override is
  // empty, copy the static defaults into the override so the admin sees
  // the current bullets as editable inputs immediately. This makes the
  // edit/remove workflow obvious (no need to re-type defaults first).
  const handleToggle = (key) => {
    if (openKey === key) {
      setOpenKey(null);
      return;
    }
    const current = getItem(key);
    const defaults = getServiceDefaults(key);
    const patch = {};
    if (!current.tagline && defaults.tagline) patch.tagline = defaults.tagline;
    if ((!current.features || current.features.length === 0) && defaults.features.length > 0) {
      patch.features = defaults.features.slice();
    }
    if ((!current.detailFeatures || current.detailFeatures.length === 0) && defaults.detailFeatures.length > 0) {
      patch.detailFeatures = defaults.detailFeatures.slice();
    }
    if (Object.keys(patch).length > 0) setItem(key, patch);
    setOpenKey(key);
  };

  const revertToDefaults = (key) => {
    if (!confirm("Vrátit tuto službu na výchozí texty?\n\nVšechny vaše úpravy této služby budou ztraceny po dalším uložení.")) return;
    setItem(key, { tagline: "", features: [], detailFeatures: [] });
  };

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="section-services-items">
      <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1">Detail služeb · 6 služeb</div>
      <p className="text-xs text-apex-cream/60 mb-4">
        Klikněte na službu — uvidíte aktuální body (předvyplněné z výchozích textů). Můžete je libovolně <strong>editovat, mazat</strong> nebo <strong>přidávat nové</strong>. Tlačítkem &bdquo;Vrátit výchozí&ldquo; obnovíte původní text dané služby.
      </p>
      <div className="space-y-3">
        {SERVICE_ITEMS.map((svc) => {
          const item = getItem(svc.key);
          const isOpen = openKey === svc.key;
          const defaults = getServiceDefaults(svc.key);
          const customTagline = item.tagline && item.tagline !== defaults.tagline;
          const featCount = (item.features || []).filter((x) => (x || "").trim()).length;
          const detCount = (item.detailFeatures || []).filter((x) => (x || "").trim()).length;
          return (
            <div key={svc.key} className="rounded-xl border border-apex-line bg-apex-bg/50">
              <button
                onClick={() => handleToggle(svc.key)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-apex-card/40 transition-colors"
                data-testid={`service-item-toggle-${svc.key}`}
              >
                <div>
                  <div className="text-sm font-semibold text-apex-cream">{svc.label}</div>
                  <div className="text-xs text-apex-cream/55 mt-0.5">
                    {customTagline ? "vlastní tagline · " : "výchozí tagline · "}
                    {featCount > 0 ? `${featCount} bodů karty · ` : "výchozí karta · "}
                    {detCount > 0 ? `${detCount} detailních bodů` : "výchozí detail"}
                  </div>
                </div>
                <span className="text-apex-gold text-xs">{isOpen ? "skrýt ▲" : "upravit ▼"}</span>
              </button>
              {isOpen && (
                <div className="p-4 border-t border-apex-line space-y-3" data-testid={`service-item-body-${svc.key}`}>
                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => revertToDefaults(svc.key)}
                      className="text-xs px-3 py-1.5 rounded-lg border border-apex-line text-apex-cream/70 hover:border-apex-gold/40 hover:text-apex-cream inline-flex items-center gap-1.5"
                      data-testid={`service-revert-${svc.key}`}
                      title="Vrátit tuto službu na výchozí texty"
                    >
                      <RotateCcw className="w-3.5 h-3.5" /> Vrátit výchozí
                    </button>
                  </div>
                  <label className="block">
                    <div className="text-xs text-apex-cream/70 mb-1">Tagline (krátký podtitulek)</div>
                    <input
                      type="text"
                      value={item.tagline || ""}
                      onChange={(e) => setItem(svc.key, { tagline: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60"
                      data-testid={`service-tagline-${svc.key}`}
                    />
                  </label>
                  <BulletEditor
                    label="Body karty · zobrazené v hlavním ceníku"
                    bullets={item.features}
                    onChange={(features) => setItem(svc.key, { features })}
                    testIdPrefix={`service-feat-${svc.key}`}
                  />
                  <BulletEditor
                    label={`Detailní body · zobrazené v modalu „Zobrazit detail"`}
                    bullets={item.detailFeatures}
                    onChange={(detailFeatures) => setItem(svc.key, { detailFeatures })}
                    testIdPrefix={`service-detail-${svc.key}`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function BulletEditor({ label, bullets, onChange, testIdPrefix }) {
  const arr = Array.isArray(bullets) ? bullets : [];
  const setAt = (i, v) => onChange(arr.map((x, idx) => (idx === i ? v : x)));
  const remove = (i) => onChange(arr.filter((_, idx) => idx !== i));
  const add = () => onChange([...arr, ""]);

  return (
    <div>
      <div className="text-xs text-apex-cream/70 mb-1.5">{label}</div>
      <div className="space-y-2">
        {arr.length === 0 && (
          <div className="text-xs italic text-apex-cream/45 px-2 py-1.5">Žádné body — přidejte první kliknutím na &bdquo;+ Přidat bod&ldquo;. (Pokud chcete obnovit původní seznam, použijte tlačítko &bdquo;Vrátit výchozí&ldquo;.)</div>
        )}
        {arr.map((b, i) => (
          <div key={i} className="flex gap-2 items-start">
            <span className="text-apex-gold text-sm mt-2.5">•</span>
            <textarea
              value={b}
              rows={1}
              onChange={(e) => setAt(i, e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60 resize-y"
              data-testid={`${testIdPrefix}-input-${i}`}
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="px-2 py-1.5 rounded-lg border border-red-500/40 text-red-300 text-xs hover:bg-red-500/10"
              data-testid={`${testIdPrefix}-remove-${i}`}
              title="Smazat tento bod"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
      <button
        type="button"
        onClick={add}
        className="mt-2 text-xs px-3 py-1.5 rounded-lg border border-apex-gold/40 text-apex-gold hover:bg-apex-gold/10 inline-flex items-center gap-1.5"
        data-testid={`${testIdPrefix}-add`}
      >
        + Přidat bod
      </button>
    </div>
  );
}

// ── Packages editor ────────────────────────────────────────────────────────
function PackagesEditor() {
  const [pkgs, setPkgs] = useState(null);
  const [meta, setMeta] = useState({ phone: "", phoneRaw: "", ctaLabel: "" });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [openId, setOpenId] = useState(null);

  const load = () => {
    setLoading(true);
    api
      .get("/admin/packages")
      .then((r) => {
        setPkgs(r.data?.packages || []);
        setMeta({
          phone: r.data?.phone || "",
          phoneRaw: r.data?.phoneRaw || "",
          ctaLabel: r.data?.ctaLabel || "",
        });
      })
      .finally(() => setLoading(false));
  };
  useEffect(load, []);

  const updatePkg = (id, patch) => setPkgs((arr) => arr.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  const updateFeatures = (id, features) => updatePkg(id, { features });

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      const payload = { packages: pkgs, phone: meta.phone, phoneRaw: meta.phoneRaw, ctaLabel: meta.ctaLabel };
      const { data } = await api.put("/admin/packages", payload);
      setPkgs(data?.packages || pkgs);
      setMsg({ kind: "ok", text: "Balíčky uloženy." });
    } catch (e) {
      setMsg({ kind: "err", text: e?.response?.data?.detail || "Uložení selhalo." });
    } finally {
      setSaving(false);
    }
  };

  if (loading || !pkgs) return <Loader2 className="w-6 h-6 animate-spin text-apex-gold" />;

  return (
    <div className="rounded-2xl bg-apex-card border border-apex-line p-5" data-testid="section-packages-edit">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-xs uppercase tracking-[0.3em] text-apex-gold/80 mb-1">Balíčky · obsah karet</div>
          <p className="text-xs text-apex-cream/60">
            Upravte text jednotlivých balíčků v sekci &bdquo;Můj účet → Výhodné balíčky&ldquo;. Body lze přidávat i mazat.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="btn-gold !py-2 !px-4 text-sm disabled:opacity-50"
          data-testid="packages-save-btn"
        >
          <Save className="w-4 h-4" /> {saving ? "Ukládá…" : "Uložit balíčky"}
        </button>
      </div>

      {msg && (
        <div
          className={`mb-3 rounded-lg px-4 py-2 text-sm ${
            msg.kind === "ok" ? "bg-emerald-500/10 text-emerald-300 border border-emerald-500/30" : "bg-red-500/10 text-red-300 border border-red-500/30"
          }`}
          data-testid="packages-msg"
        >
          {msg.kind === "ok" && <Check className="w-3.5 h-3.5 inline mr-1.5" />}
          {msg.text}
        </div>
      )}

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Telefon (zobrazený)</div>
          <input type="text" value={meta.phone} onChange={(e) => setMeta((m) => ({ ...m, phone: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="packages-phone" />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">Telefon (tel: link)</div>
          <input type="text" value={meta.phoneRaw} onChange={(e) => setMeta((m) => ({ ...m, phoneRaw: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="packages-phone-raw" />
        </label>
        <label className="block">
          <div className="text-xs text-apex-cream/70 mb-1">CTA štítek</div>
          <input type="text" value={meta.ctaLabel} onChange={(e) => setMeta((m) => ({ ...m, ctaLabel: e.target.value }))} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm" data-testid="packages-cta-label" />
        </label>
      </div>

      <div className="space-y-3">
        {pkgs.map((p) => {
          const isOpen = openId === p.id;
          return (
            <div key={p.id} className="rounded-xl border border-apex-line bg-apex-bg/50">
              <button
                onClick={() => setOpenId(isOpen ? null : p.id)}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left hover:bg-apex-card/40 transition-colors"
                data-testid={`package-toggle-${p.id}`}
              >
                <div>
                  <div className="text-sm font-semibold text-apex-cream">{p.name} <span className="text-apex-cream/55 text-xs">· {p.priceLabel}</span></div>
                  <div className="text-xs text-apex-cream/55 mt-0.5">{p.frequency} · {(p.features || []).length} bodů {p.popular ? "· nejoblíbenější" : ""}</div>
                </div>
                <span className="text-apex-gold text-xs">{isOpen ? "skrýt ▲" : "upravit ▼"}</span>
              </button>
              {isOpen && (
                <div className="p-4 border-t border-apex-line space-y-3" data-testid={`package-body-${p.id}`}>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Název" value={p.name} onChange={(v) => updatePkg(p.id, { name: v })} testId={`package-${p.id}-name`} />
                    <Field label="Tagline" value={p.tagline} onChange={(v) => updatePkg(p.id, { tagline: v })} testId={`package-${p.id}-tagline`} />
                    <Field label="Cena (štítek)" value={p.priceLabel} onChange={(v) => updatePkg(p.id, { priceLabel: v })} testId={`package-${p.id}-price`} />
                    <Field label="Frekvence" value={p.frequency} onChange={(v) => updatePkg(p.id, { frequency: v })} testId={`package-${p.id}-freq`} />
                  </div>
                  <Field label="Podtitulek (delší)" value={p.subtitle} onChange={(v) => updatePkg(p.id, { subtitle: v })} multiline testId={`package-${p.id}-subtitle`} />
                  <label className="inline-flex items-center gap-2 text-xs text-apex-cream/80">
                    <input
                      type="checkbox"
                      checked={!!p.popular}
                      onChange={(e) => updatePkg(p.id, { popular: e.target.checked })}
                      data-testid={`package-${p.id}-popular`}
                    />
                    Označit jako &bdquo;Nejoblíbenější&ldquo;
                  </label>
                  <BulletEditor
                    label="Body balíčku"
                    bullets={p.features || []}
                    onChange={(features) => updateFeatures(p.id, features)}
                    testIdPrefix={`package-${p.id}-feat`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, testId, multiline = false }) {
  return (
    <label className="block">
      <div className="text-xs text-apex-cream/70 mb-1">{label}</div>
      {multiline ? (
        <textarea value={value || ""} rows={2} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60 resize-y" data-testid={testId} />
      ) : (
        <input type="text" value={value || ""} onChange={(e) => onChange(e.target.value)} className="w-full px-3 py-2 rounded-lg bg-apex-bg border border-apex-line text-apex-cream text-sm focus:outline-none focus:border-apex-gold/60" data-testid={testId} />
      )}
    </label>
  );
}
